/**
 * theme.js — OPD doctor-console theme engine (no JSX; only the Medplum prefs
 * imports below).
 *
 * Defines the full --opd-* CSS custom-property palette on :root (light) and
 * html[data-theme="dark"] (dark), derives the primary ramp from the hospital
 * brand colour, and exposes window.OPDTheme = { get, resolved, set, toggle }.
 *
 * Load EARLY (main.jsx imports it first) so the stored theme is applied
 * before first paint — no flash. Safe on pages without OPDBridge /
 * __OPD_DATA__.
 *
 * Persistence: localStorage 'healther.opd.theme' ('light'|'dark'|'system'),
 * plus a best-effort savePrefs({ theme }) to the doctor's own prefs Basic in
 * Medplum when a staff session exists (RLS Basic?author=%profile — see
 * medplum-settings.js). Every apply dispatches  window 'opd:theme'  with
 * { detail: { mode, resolved } }  (resolved is always 'light'|'dark').
 */
import { authHeader } from './medplum-client.js';
import { savePrefs } from './medplum-settings.js';

(function () {
  'use strict';
  if (window.OPDTheme) return; // idempotent — pages may double-include

  var KEY = 'healther.opd.theme';
  var DEFAULT_BRAND = '#0F6D8C'; // current primary-600 blue
  var MODES = ['light', 'dark', 'system'];

  // ── tiny colour helpers ────────────────────────────────────
  function hexToRgb(hex) {
    var h = String(hex || '').trim().replace(/^#/, '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  function rgbToHex(rgb) {
    return '#' + rgb.map(function (c) {
      var v = Math.max(0, Math.min(255, Math.round(c))).toString(16);
      return v.length === 1 ? '0' + v : v;
    }).join('');
  }
  // mix(a → b, t in 0..1): t=0 gives a, t=1 gives b
  function mix(a, b, t) {
    var ra = hexToRgb(a), rb = hexToRgb(b);
    if (!ra || !rb) return a;
    return rgbToHex([0, 1, 2].map(function (i) { return ra[i] + (rb[i] - ra[i]) * t; }));
  }
  function alpha(hex, a) {
    var rgb = hexToRgb(hex);
    if (!rgb) return 'rgba(15,109,140,' + a + ')';
    return 'rgba(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ',' + a + ')';
  }
  // hex → shadcn "H S% L%" triplet (consumed via hsl(var(--primary))). Returns
  // null on a bad hex so callers can fall back to the mockup default.
  function hexToHsl(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return null;
    var r = rgb[0] / 255, g = rgb[1] / 255, b = rgb[2] / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    var d = max - min;
    if (d !== 0) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return Math.round(h * 360) + ' ' + Math.round(s * 100) + '% ' + Math.round(l * 100) + '%';
  }
  // Relative luminance (sRGB) → pick a legible foreground for a tinted button.
  function luminance(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return 0;
    return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  }

  // ── palette ───────────────────────────────────────────────
  function paletteCss(brand) {
    var b = hexToRgb(brand) ? brand : DEFAULT_BRAND;
    var darkBase = '#10161F';
    var light = {
      // surfaces
      'canvas': '#FAFBFC', 'surface': '#FFFFFF', 'subtle': '#F3F5F7', 'sunken': '#EDF0F3',
      // text
      'text': '#0F1F2C', 'text-secondary': '#4A5968', 'text-muted': '#7A8896', 'text-disabled': '#B4BEC8',
      'on-primary': '#FFFFFF',
      // borders
      'border-subtle': '#E3E8ED', 'border': '#CDD5DD', 'border-strong': '#9AA7B4',
      // primary ramp — derived from the hospital brand colour
      'brand': b,
      'primary-50': mix(b, '#FFFFFF', 0.92),
      'primary-100': mix(b, '#FFFFFF', 0.84),
      'primary-500': mix(b, '#FFFFFF', 0.12),
      'primary-600': b,
      'primary-700': mix(b, '#000000', 0.24),
      'primary-glow': alpha(b, 0.25),
      // AI (amber) family
      'ai-bg': '#FEF7EC', 'ai-border': '#E8A13D', 'ai-chip': '#FCEBC8', 'ai-text': '#8B5A0B',
      'ai-line': '#F1DFB9', 'ai-foot': '#FBF1DE', 'ai-track': '#F4E6CC',
      // success / verified
      'success': '#2E9D6B', 'success-bg': '#EAF6F0', 'success-chip': '#CBE8D7', 'success-text': '#0E5E3A',
      // warn / danger / info
      'warn': '#C9810F', 'warn-bg': '#FFF4E0',
      'danger': '#C8342B', 'danger-bg': '#FDECEA', 'danger-text': '#7A1E18', 'danger-border': '#F3C9C5',
      'info': '#2D6FBF', 'info-bg': '#E8F1FB',
      'recording': '#D93025',
      // chrome
      'scrim': 'rgba(15,31,44,0.45)',
      'shadow-sm': '0 1px 2px rgba(15,31,44,0.06)',
      'shadow-md': '0 4px 12px rgba(15,31,44,0.08)',
      'shadow-lg': '0 12px 32px rgba(15,31,44,0.16)',
    };
    var dark = {
      'canvas': '#0E1420', 'surface': '#161F2B', 'subtle': '#1D2937', 'sunken': '#111A26',
      'text': '#E8EEF4', 'text-secondary': '#ABB8C5', 'text-muted': '#8593A2', 'text-disabled': '#5A6878',
      'on-primary': '#FFFFFF',
      'border-subtle': '#243140', 'border': '#324153', 'border-strong': '#4A5C71',
      'brand': b,
      'primary-50': mix(b, darkBase, 0.85),
      'primary-100': mix(b, darkBase, 0.74),
      'primary-500': mix(b, '#FFFFFF', 0.22),
      'primary-600': b,
      'primary-700': mix(b, '#FFFFFF', 0.55), // used as text on tinted chips — must be light
      'primary-glow': alpha(b, 0.4),
      'ai-bg': '#251D10', 'ai-border': '#C9913B', 'ai-chip': '#3A2D15', 'ai-text': '#E8B765',
      'ai-line': '#3D331B', 'ai-foot': '#2B2212', 'ai-track': '#352A14',
      'success': '#3BA776', 'success-bg': '#122620', 'success-chip': '#1B3A2D', 'success-text': '#86D7AD',
      'warn': '#E3A43F', 'warn-bg': '#2B2110',
      'danger': '#E15648', 'danger-bg': '#2C1614', 'danger-text': '#F4A79E', 'danger-border': '#4D2320',
      'info': '#6FA8EA', 'info-bg': '#15243B',
      'recording': '#E5544B',
      'scrim': 'rgba(0,0,0,0.55)',
      'shadow-sm': '0 1px 2px rgba(0,0,0,0.4)',
      'shadow-md': '0 4px 14px rgba(0,0,0,0.5)',
      'shadow-lg': '0 16px 40px rgba(0,0,0,0.6)',
    };
    function block(map) {
      return Object.keys(map).map(function (k) { return '  --opd-' + k + ': ' + map[k] + ';'; }).join('\n');
    }
    // IPD world tone (owner-directed 2026-07-07): while the staff shell stamps
    // <html data-world="ipd">, the primary ramp shifts to the muted ward-indigo
    // (#4756A6 family — matches the static block in colors_and_type.css). It
    // must be emitted HERE, inside this injected sheet and AFTER the base
    // blocks, because this sheet otherwise clobbers the static [data-world]
    // rules (runtime injection + html[data-theme="dark"] specificity). The
    // world ramp is deliberately NOT brand-derived — the ward is "a different
    // building" regardless of the hospital brand. Only the ramp shifts; every
    // other token stays the theme's own.
    var IPD = '#4756A6';
    var ipdLight = {
      'primary-50': '#EEF1FA', 'primary-100': '#DDE3F5',
      'primary-500': '#5A6BC4', 'primary-600': IPD, 'primary-700': '#38468C',
      'primary-glow': alpha(IPD, 0.25),
    };
    var ipdDark = {
      'primary-50': mix(IPD, darkBase, 0.85),
      'primary-100': mix(IPD, darkBase, 0.74),
      'primary-500': mix(IPD, '#FFFFFF', 0.22),
      'primary-600': mix(IPD, '#FFFFFF', 0.08), // slight lift for dark contrast
      'primary-700': mix(IPD, '#FFFFFF', 0.55), // text on tinted chips — light
      'primary-glow': alpha(IPD, 0.4),
    };
    // ── shadcn/ui monochrome (2026-07-09, owner decision) ──────
    // The rebuild's shadcn components stay shadcn zinc MONOCHROME everywhere —
    // near-black --primary in light, near-white in dark — set statically in
    // shadcn.css. The hospital brand colour deliberately NO LONGER emits a
    // --primary/--ring override here; it keeps affecting ONLY the legacy
    // --opd-* namespace above. This is what makes every default Button/active
    // element render like ui.shadcn.com instead of tinted teal.
    return ':root {\n' + block(light) + '\n  color-scheme: light;\n}\n' +
      'html[data-theme="dark"] {\n' + block(dark) + '\n  color-scheme: dark;\n}\n' +
      'html[data-world="ipd"] {\n' + block(ipdLight) + '\n}\n' +
      'html[data-theme="dark"][data-world="ipd"] {\n' + block(ipdDark) + '\n}\n';
  }

  // ── style injection ───────────────────────────────────────
  var brandColor = DEFAULT_BRAND;
  function injectVars() {
    var el = document.getElementById('opd-theme-vars');
    if (!el) {
      el = document.createElement('style');
      el.id = 'opd-theme-vars';
      (document.head || document.documentElement).appendChild(el);
    }
    el.textContent = paletteCss(brandColor);
  }

  // ── mode handling ─────────────────────────────────────────
  var mq = null;
  try { mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)'); } catch (e) {}
  var stored = null;
  try { stored = localStorage.getItem(KEY); } catch (e) {}
  var mode = MODES.indexOf(stored) >= 0 ? stored : null; // null → not chosen on this device yet

  function resolved(m) {
    var v = m || mode || 'light';
    if (v === 'system') return (mq && mq.matches) ? 'dark' : 'light';
    return v;
  }

  function applyDom() {
    var r = resolved();
    try {
      document.documentElement.setAttribute('data-theme', r);
      document.documentElement.style.colorScheme = r;
    } catch (e) {}
    try {
      window.dispatchEvent(new CustomEvent('opd:theme', { detail: { mode: mode || 'light', resolved: r } }));
    } catch (e) {}
  }

  // A live staff Medplum session = a bearer token in storage (sync check, no
  // client construction) — anon kiosk/board pages have none and skip persist.
  function hasStaffSession() {
    try { return !!authHeader().Authorization; } catch (e) { return false; }
  }

  function set(next, opts) {
    if (MODES.indexOf(next) < 0) return;
    mode = next;
    var persist = !opts || opts.persist !== false;
    if (persist) {
      try { localStorage.setItem(KEY, next); } catch (e) {}
      if (hasStaffSession()) {
        // Best-effort cross-device default (the doctor's prefs Basic in
        // Medplum, written run-as-you) — never blocks, never throws.
        try { savePrefs({ theme: next }).catch(function () {}); } catch (e) {}
      }
    }
    applyDom();
  }

  if (mq) {
    var onMq = function () { if ((mode || 'light') === 'system') applyDom(); };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  }

  window.OPDTheme = {
    get: function () { return mode || 'light'; },
    resolved: function () { return resolved(); },
    set: function (m) { set(m); },
    toggle: function () { set(resolved() === 'dark' ? 'light' : 'dark'); },
    setBrand: function (hex) { if (hexToRgb(hex)) { brandColor = hex; injectVars(); } },
  };

  // Pre-paint: vars + stored (device) theme immediately.
  injectVars();
  applyDom();

  // After the bootstrap shim has run: fall back to the server-stored default
  // (only when this device has no choice yet) and adopt the hospital brand.
  function lateInit() {
    try {
      if (!stored || MODES.indexOf(stored) < 0) {
        var s = window.__OPD_DATA__ && window.__OPD_DATA__.settings && window.__OPD_DATA__.settings.theme;
        if (MODES.indexOf(s) >= 0 && s !== (mode || 'light')) set(s, { persist: false });
      }
      var brand = (window.OPDBridge && window.OPDBridge.hospital && window.OPDBridge.hospital.brandColor)
        || (window.__OPD_DATA__ && window.__OPD_DATA__.hospital && window.__OPD_DATA__.hospital.brandColor);
      if (brand) window.OPDTheme.setBrand(brand);
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lateInit);
  else lateInit();
})();
